import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ProjectService } from '../../../../../../common/services/project.service';
import { Contract, CreateContract, ProjectType, UpdateContract } from '../../../../../../common/models/project';
import { Subject, takeUntil } from 'rxjs';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
  selector: 'app-contract-detail',
  templateUrl: './contract.component.html',
  styleUrls: ['./contract.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDatepickerModule
]
})
export class ContractComponent implements OnInit {
  private destroy$ = new Subject<void>();
  isHandset: boolean = false;

  contractForm: FormGroup;
  isUpdating: boolean = false;
  projectId!: string;
  projectType: ProjectType = ProjectType.Application;
  contractId!: string;

  public ProjectType = ProjectType;

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private snackBar: MatSnackBar,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {

    this.contractForm = this.fb.group({
      classification: [null, Validators.required],
      refNumber: [null, Validators.required],
      number: [null],
      account: [null],
      activity: [null],
      date: [null],
      //Allocation only
      decisionClassificationCode: [null],
      decisionReferenceNumber: [null],
      decisionDate: [null]
    });
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$)).subscribe((result) => {
        this.isHandset = result.matches;
      });

    this.projectId = this.route.snapshot.paramMap.get('projectId') || '';
    const contractId = this.route.snapshot.paramMap.get('contractId');

    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state || history.state;
    if (state && state.projectType !== undefined) {
      this.projectType = state.projectType as ProjectType;
    }

    if (contractId) {
      this.isUpdating = true;
      this.contractId = contractId;
      this.loadContract(this.projectId, this.contractId);
    } else {
      this.isUpdating = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadContract(projectId: string, contractId: string) {
    this.projectService.getContract(projectId, contractId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (contract: Contract) => {
        this.contractForm.patchValue({
          classification: contract.classification,
          number: contract.number,
          refNumber: contract.refNumber,
          account: contract.account,
          activity: contract.activity,
          date: contract.date,
          //Allocation only
          decisionClassificationCode: contract.decisionClassificationCode,
          decisionReferenceNumber: contract.decisionReferenceNumber,
          decisionDate: contract.decisionDate
        });
      },
      error: (err) => {
        console.error('Error loading contract', err);
        this.snackBar.open(
          'Dogodila se greška prilikom učitavanja ugovora.', 'Zatvori', this.snackbarConfig
        );
      }
    });
  }

  onSubmit() {
    if (this.contractForm.invalid) {
      this.snackBar.open('Molimo ispunite sva obavezna polja prije spremanja.', 'Zatvori', this.snackbarConfig );
      return;
    }

    if (this.isUpdating) {
      this.updateContract();
    } else {
      this.createContract();
    }
  }

  private updateContract() {
    const dto: UpdateContract = {
      ...this.contractForm.value
    };

    this.projectService
    .updateContract(this.projectId, this.contractId, dto).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Uspješno ste ažurirali ugovor.', 'Zatvori', this.snackbarConfig );
           this.router.navigate(['municipality', 'projects', this.projectId, 'contracts'], {
            state: { projectType: this.projectType }
          });
        },
        error: (error) => {
          console.error('Error updating contract', error);
          this.snackBar.open('Greška prilikom ažuriranja ugovora', 'Zatvori',  this.snackbarConfig);
        }
      });
  }

  private createContract() {
    const dto: CreateContract = {
      ...this.contractForm.value
    };

    this.projectService.createContract(this.projectId, dto).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Uspješno ste dodali novi ugovor.', 'Zatvori', this.snackbarConfig);
         this.router.navigate(['municipality', 'projects', this.projectId, 'contracts'], {
          state: { projectType: this.projectType }
        });
      },
      error: (error) => {
        console.error('Error creating contract', error);
        this.snackBar.open('Greška prilikom kreiranja ugovora', 'Zatvori', this.snackbarConfig);
      }
    });
  }
}
